import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IoClose } from "react-icons/io5";
import {
  Popover as RsPopover,
  PopoverBody,
  PopoverHeader,
  PopoverProps as RsPopoverProps,
} from "reactstrap";

import { PopoverProps } from "@models/components/popover.model";

const resolveTargetElement = (
  target:
    | HTMLElement
    | RefObject<HTMLElement | null>
    | string
    | null
    | undefined
): HTMLElement | null => {
  if (typeof document === "undefined" || !target) return null;

  if (typeof target !== "string") {
    if ("current" in target) {
      return target.current;
    }
    return target;
  }

  // 1. Direct ID or query selector lookup
  const direct =
    document.getElementById(target) || document.querySelector(target);
  if (direct) return direct as HTMLElement;

  if (!target.startsWith("#") && !target.startsWith(".")) {
    try {
      const byHash = document.querySelector(`#${target}`);
      if (byHash) return byHash as HTMLElement;
    } catch {
      // ignore invalid selector syntax
    }
  }

  return null;
};

const resolveContainerElement = (
  container:
    | HTMLElement
    | RefObject<HTMLElement | null>
    | string
    | null
    | undefined
): HTMLElement | undefined => {
  if (typeof document === "undefined" || !container) return undefined;

  if (typeof container !== "string") {
    if ("current" in container) {
      return container.current || undefined;
    }
    return container;
  }

  try {
    const el = document.querySelector(container);
    if (el) return el as HTMLElement;
  } catch {
    // ignore invalid selector syntax
  }

  return undefined;
};

export const Popover: FC<PopoverProps> = ({
  arrowClassName,
  bodyClassName,
  children,
  className,
  container,
  fade = true,
  footer,
  footerClassName,
  headerClassName,
  hideArrow = false,
  isOpen,
  modifiers,
  offset = [0, 8],
  onClose,
  placement = "auto",
  placementPrefix = "bs-popover",
  popperClassName,
  showCloseButton = true,
  target,
  testId = "fg-popover",
  title,
  toggle,
  zIndex = 1060,
}) => {
  const popoverRef = useRef<HTMLElement | null>(null);
  const lastTargetRef = useRef<HTMLElement | null>(null);

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(() =>
    resolveTargetElement(target)
  );

  useEffect(() => {
    if (!target) {
      setTargetElement(null);
      lastTargetRef.current = null;
      return;
    }

    let observer: MutationObserver | null = null;
    let animationFrameId: number | null = null;

    const findTarget = () => {
      const found = resolveTargetElement(target);
      if (found) {
        setTargetElement(found);
        lastTargetRef.current = found;
        if (observer) observer.disconnect();
        return true;
      }
      return false;
    };

    if (!findTarget()) {
      if (typeof MutationObserver !== "undefined") {
        observer = new MutationObserver(() => {
          findTarget();
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
      animationFrameId = requestAnimationFrame(() => {
        findTarget();
      });
    }

    return () => {
      if (observer) observer.disconnect();
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [target, isOpen]);

  const synchronousTarget = resolveTargetElement(target);
  if (synchronousTarget) {
    lastTargetRef.current = synchronousTarget;
  }
  const activeTarget = target
    ? synchronousTarget || targetElement || lastTargetRef.current
    : null;
  const isTargetConnected = Boolean(
    activeTarget &&
      (typeof activeTarget.isConnected === "undefined" ||
        activeTarget.isConnected)
  );

  const handleToggle = useCallback(() => {
    if (toggle) {
      toggle();
    } else if (onClose) {
      onClose();
    }
  }, [toggle, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentClick = (e: MouseEvent) => {
      const clickTarget = e.target as Node | null;
      if (!clickTarget) return;

      // Ignore clicks on or inside the target trigger element
      if (
        activeTarget &&
        (activeTarget === clickTarget || activeTarget.contains(clickTarget))
      ) {
        return;
      }

      // Ignore clicks inside the popover itself
      if (popoverRef.current && popoverRef.current.contains(clickTarget)) {
        return;
      }

      const popoverEl = document.querySelector(
        `[data-testid="${testId}"], .fg-custom-popover`
      );
      if (popoverEl && popoverEl.contains(clickTarget)) {
        return;
      }

      // Trigger closing immediately on outside click in capture phase
      handleToggle();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleToggle();
      }
    };

    document.addEventListener("click", handleDocumentClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen, activeTarget, testId, handleToggle]);

  const popperModifiers = useMemo(() => {
    const defaultModifiers = [
      {
        name: "preventOverflow",
        options: {
          boundary: "clippingParents",
          padding: 8,
        },
      },
      {
        name: "flip",
        options: {
          boundary: "clippingParents",
          padding: 8,
        },
      },
    ];
    return (
      modifiers ? [...defaultModifiers, ...modifiers] : defaultModifiers
    ) as RsPopoverProps["modifiers"];
  }, [modifiers]);

  // If no target has ever been resolved for this popover, or target is disconnected from DOM, don't render RsPopover
  if (!activeTarget || !isTargetConnected) {
    return null;
  }

  // If not open and no fade animation is needed, unmount immediately
  if (!isOpen && !fade) {
    return null;
  }

  const hasHeader = Boolean(title || (showCloseButton && (onClose || toggle)));

  return (
    <RsPopover
      target={activeTarget}
      container={resolveContainerElement(container)}
      isOpen={isOpen}
      toggle={handleToggle}
      innerRef={popoverRef}
      placement={placement}
      placementPrefix={placementPrefix}
      popperClassName={popperClassName}
      className={`fg-custom-popover bs-popover-auto ${className || ""}`.trim()}
      offset={offset}
      modifiers={popperModifiers}
      style={{ zIndex }}
      data-testid={testId}
      fade={fade}
      hideArrow={hideArrow}
      arrowClassName={arrowClassName}
      trigger="legacy"
    >
      {hasHeader && (
        <PopoverHeader
          className={`d-flex align-items-center justify-content-between ${headerClassName || ""}`}
          data-testid={`${testId}-header`}
        >
          {title && <span className="fg-popover-title">{title}</span>}
          {showCloseButton && (onClose || toggle) && (
            <button
              type="button"
              className="fg-popover-close-btn ms-auto"
              aria-label="Close"
              onClick={handleToggle}
              data-testid={`${testId}-close-btn`}
            >
              <IoClose size={18} />
            </button>
          )}
        </PopoverHeader>
      )}
      <PopoverBody className={bodyClassName} data-testid={`${testId}-body`}>
        {children}
      </PopoverBody>
      {footer && (
        <div
          className={`fg-popover-footer p-2 border-top ${footerClassName || ""}`}
          data-testid={`${testId}-footer`}
        >
          {footer}
        </div>
      )}
    </RsPopover>
  );
};

export default Popover;
